/* eslint-disable no-nested-ternary */
import { type KeyboardEvent, type ReactElement } from 'react'
import { StackIcons, CircleSolidIcon, CloseIcon } from '@dhl-ui/icons'
import { spacing } from '../../theme'
import { type DataTestIdAttribute } from '../../types'
import { Ellipsis } from '../Ellipsis'
import { Box } from '../Box'
import { isDeleteKeyboardEvent } from './is-delete-keyboard-event'
import { getStyles, type StylesProps } from './get-styles'
import { isNotDefault } from './is-not-default'

export type ChipProps = {
  label: string

  /**
   * You are able to place our Avatar or any icon from @dhl-ui/icons
   */
  startNode?: ReactElement | null

  color?: 'default' | 'primary' | 'error' | 'info' | 'success' | 'warning' | 'dark'

  disabled?: boolean
  isSquare?: boolean
  size?: 'small' | 'medium'
  variant?: 'filled' | 'outlined'

  sx?: StylesProps['sx']

  onClick?: () => void
  onDelete?: (event?: Event) => void
} & DataTestIdAttribute

export const Chip = ({
  label,
  startNode = null,
  size = 'medium',
  color = 'default',
  variant = 'filled',
  isSquare = false,
  disabled = false,
  onClick,
  onDelete,
  sx,
  ...rest
}: ChipProps) => {
  const iconSize = size === 'medium' ? spacing(2.5) : spacing(1.8)
  const isClickable = !!(onClick || onDelete) && !disabled
  const hasColor = isNotDefault(color)

  const handleKeyUp = (event: KeyboardEvent<HTMLSpanElement>) => {
    if (event.currentTarget === event.target) {
      if (onDelete && isDeleteKeyboardEvent(event)) {
        onDelete()
      }

      if (onClick && event.key === 'Enter') {
        onClick()
      }
    }
  }

  return (
    <Box
      tabIndex={isClickable ? 0 : -1}
      onKeyUp={isClickable ? handleKeyUp : undefined}
      onClick={onClick && !disabled ? onClick : undefined}
      component="span"
      aria-hidden={disabled ? 'true' : 'false'}
      sx={({ misc }) =>
        getStyles({
          sx,
          misc,
          spacing,
          size,
          isSquare,
          isClickable,
          variant,
          color,
          disabled,
        })
      }
      {...rest}
    >
      <Box
        sx={{
          display: 'inline-flex',
          svg: {
            width: spacing(2),
            height: spacing(2),
          },
        }}
        component="span"
      >
        {startNode}
      </Box>
      <Box
        sx={{
          px: spacing(0.75),
          display: 'flex',
          // FIXME: not working cause of wrapping
          alignContent: 'center',
        }}
        component="span"
      >
        <Ellipsis variant={size === 'small' ? 'caption1' : 'body2'}>{label}</Ellipsis>
      </Box>
      {onDelete && (
        <StackIcons
          variant="centered"
          sx={{ mr: '1px', fontSize: iconSize }}
          onClick={(e) => {
            e.stopPropagation()
            onDelete()
          }}
        >
          <CircleSolidIcon
            sx={{
              color: hasColor ? (variant === 'outlined' ? `${color}.main` : `${color}.light`) : 'alternative.dark',
            }}
          />
          <CloseIcon
            sx={{
              color: hasColor ? (variant === 'outlined' ? 'pure.light' : 'pure.dark') : 'pure.light',
            }}
          />
        </StackIcons>
      )}
    </Box>
  )
}
